import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Input, Pagination, Select, Button as AntButton, Empty } from 'antd';
import {
    SearchOutlined,
    FilterOutlined,
    MoreOutlined,
    DownOutlined,
    PlusOutlined,
    InboxOutlined,
} from '@ant-design/icons';
import PriceBadge from '@/components/ui/PriceBadge';
import StatusBadge from '@/components/ui/StatusBadge';
import {
    TableWrapper,
    TableContent,
    StyledTable,
    TableHeaderCell,
    TableBody,
    TableRow,
    TableCell,
    TableEmptyCell,
    TablePagination,
    StyledPagination,
} from './style';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableRow = ({
    record,
    rowIndex,
    columns,
    renderCell,
    pagination,
    effectivePage,
    effectivePageSize,
    onRow,
}) => {
    const rowKey = record.key || record.id || String(rowIndex);
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: rowKey });

    const actualIndex = pagination ? (effectivePage - 1) * effectivePageSize + rowIndex : rowIndex;
    const rowProps = onRow ? onRow(record, actualIndex) : {};
    const { style: rowStyle, ...otherRowProps } = rowProps;

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.001 : 1,
        ...rowStyle,
        cursor: isDragging ? 'grabbing' : 'pointer',
    };

    return (
        <TableRow
            ref={setNodeRef}
            style={style}
            $clickable={!!onRow}
            {...otherRowProps}
            {...attributes}
            {...listeners}
        >
            {columns.map((column, colIndex) => (
                <TableCell
                    key={column.key || column.dataIndex || colIndex}
                    $align='left'
                    $width={column.width}
                >
                    {renderCell(column, record, actualIndex)}
                </TableCell>
            ))}
        </TableRow>
    );
};

const Table = ({
    columns,
    dataSource,
    pagination = true,
    pageSize: initialPageSize = 10,
    $topMargin = true,
    $radius,
    onRow,
    paginationTotal,
    paginationCurrent,
    onPaginationChange,
    sortable = false,
    onReorder,
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const [activeId, setActiveId] = useState(null);
    const wasDraggingRef = useRef(false);

    const isServerPagination = typeof paginationTotal === 'number' && typeof onPaginationChange === 'function';
    const effectivePage = isServerPagination && paginationCurrent != null ? paginationCurrent : currentPage;
    const effectivePageSize = isServerPagination ? initialPageSize : pageSize;

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 200,
                tolerance: 5,
            },
        })
    );

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
        wasDraggingRef.current = true;
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);
        setTimeout(() => {
            wasDraggingRef.current = false;
        }, 100);

        if (over && active.id !== over.id) {
            const oldIndex = dataSource.findIndex((item) => (item.key || item.id) === active.id);
            const newIndex = dataSource.findIndex((item) => (item.key || item.id) === over.id);
            if (onReorder) {
                const newData = arrayMove(dataSource, oldIndex, newIndex);
                setTimeout(() => {
                    onReorder(newData, active.id, over.id);
                }, 100);
            }
        }
    };

    const handleDragCancel = () => {
        setActiveId(null);
        setTimeout(() => {
            wasDraggingRef.current = false;
        }, 100);
    };

    const activeRecord = useMemo(() => {
        if (!activeId) return null;
        return dataSource.find((item) => (item.key || item.id) === activeId);
    }, [dataSource, activeId]);

    const activeIndex = useMemo(() => {
        if (!activeId) return -1;
        return dataSource.findIndex((item) => (item.key || item.id) === activeId);
    }, [dataSource, activeId]);

    const wrappedOnRow = useCallback((record, index) => {
        const rowProps = onRow ? onRow(record, index) : {};
        const originalOnClick = rowProps.onClick;
        return {
            ...rowProps,
            onClick: (e) => {
                if (wasDraggingRef.current) {
                    return;
                }
                if (originalOnClick) {
                    originalOnClick(e);
                }
            }
        };
    }, [onRow]);

    // Calculate paginated data: no slice when server-side
    const paginatedData = useMemo(() => {
        if (!pagination) return dataSource;
        if (isServerPagination) return dataSource;
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return dataSource.slice(startIndex, endIndex);
    }, [dataSource, currentPage, pageSize, pagination, isServerPagination]);

    const handlePageChange = (page, size) => {
        if (isServerPagination) {
            onPaginationChange(page, size ?? effectivePageSize);
            return;
        }
        setCurrentPage(page);
        if (size && size !== pageSize) {
            setPageSize(size);
        }
    };
    const renderCell = (column, record, index) => {
        // If column has a custom render function, use it
        if (column.render) {
            return column.render(record[column.dataIndex], record, index);
        }

        // Special handling for plan column - use price badge
        if (column.dataIndex === 'plan') {
            return <PriceBadge>{record[column.dataIndex]}</PriceBadge>;
        }

        // Special handling for status column - use status badge
        if (column.dataIndex === 'status') {
            return <StatusBadge status={record[column.dataIndex]}>{record[column.dataIndex]}</StatusBadge>;
        }

        // Default: return the value
        return record[column.dataIndex];
    };

    return (
        <TableWrapper $topMargin={$topMargin} $radius={$radius}>
            <TableContent>
                <StyledTable>
                    <thead>
                        <tr>
                            {columns.map((column, index) => (
                                <TableHeaderCell
                                    key={column.key || column.dataIndex || index}
                                    $align={column.align ?? (index === 0 ? 'left' : 'center')}
                                >
                                    {column.title}
                                </TableHeaderCell>
                            ))}
                        </tr>
                    </thead>
                    <TableBody>
                        {paginatedData.length === 0 ? (
                            <TableRow>
                                <TableEmptyCell colSpan={columns.length}>
                                    <Empty
                                        image={<InboxOutlined style={{ fontSize: 48 }} />}
                                        description="No data found"
                                        styles={{ image: { height: 48 } }}
                                    />
                                </TableEmptyCell>
                            </TableRow>
                        ) : sortable ? (
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragStart={handleDragStart}
                                onDragEnd={handleDragEnd}
                                onDragCancel={handleDragCancel}
                            >
                                <SortableContext items={paginatedData.map((r) => r.key || r.id)} strategy={verticalListSortingStrategy}>
                                    {paginatedData.map((record, rowIndex) => (
                                        <SortableRow
                                            key={record.key || record.id || rowIndex}
                                            record={record}
                                            rowIndex={rowIndex}
                                            columns={columns}
                                            renderCell={renderCell}
                                            pagination={pagination}
                                            effectivePage={effectivePage}
                                            effectivePageSize={effectivePageSize}
                                            onRow={wrappedOnRow}
                                        />
                                    ))}
                                </SortableContext>
                                <DragOverlay>
                                    {activeRecord ? (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                            <tbody>
                                                <TableRow
                                                    style={{
                                                        background: 'var(--ant-color-bg-container, #ffffff)',
                                                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                                                        opacity: 0.95,
                                                        border: '1px solid rgba(0, 0, 0, 0.06)',
                                                        cursor: 'grabbing',
                                                    }}
                                                >
                                                    {columns.map((column, colIndex) => (
                                                        <TableCell
                                                            key={column.key || column.dataIndex || colIndex}
                                                            $align='left'
                                                            $width={column.width}
                                                            style={{
                                                                background: 'var(--ant-color-bg-container, #ffffff)',
                                                            }}
                                                        >
                                                            {renderCell(column, activeRecord, activeIndex)}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </tbody>
                                        </table>
                                    ) : null}
                                </DragOverlay>
                            </DndContext>
                        ) : (
                            paginatedData.map((record, rowIndex) => {
                                const actualIndex = pagination ? (effectivePage - 1) * effectivePageSize + rowIndex : rowIndex;
                                const rowProps = wrappedOnRow ? wrappedOnRow(record, actualIndex) : {};
                                return (
                                    <TableRow key={record.key || actualIndex} $clickable={!!onRow} {...rowProps}>
                                        {columns.map((column, colIndex) => (
                                            <TableCell
                                                key={column.key || column.dataIndex || colIndex}
                                                $align='left'
                                                $width={column.width}
                                            >
                                                {renderCell(column, record, actualIndex)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </StyledTable>
                {pagination && (dataSource.length > 0 || (isServerPagination && (paginationTotal ?? 0) > 0)) && (
                    <TablePagination>
                        <StyledPagination
                            current={effectivePage}
                            pageSize={effectivePageSize}
                            total={isServerPagination ? paginationTotal : dataSource.length}
                            onChange={handlePageChange}
                            onShowSizeChange={handlePageChange}
                            showSizeChanger
                            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                        />
                    </TablePagination>
                )}
            </TableContent>
        </TableWrapper>
    );
};

export default Table;
